import { isNull, removeObjectProperty } from "@/util";
import {
  ChargerConfig,
  MDBSlaveConfig,
  OperationMode,
  PulseConfig,
  ToPayConfig,
  VendingConfig,
  VendingTerminalResponse
} from "./api";

/**
 * Este servicio corrige las configuraciones antiguas con las nuevas propiedades asignando valores por defecto.
 * Es necesario hacer esto porque hay propiedades que ya no existen y otras nuevas que no son opcionales.
 *
 * @param loadedConfig configuración descargada del servicio web
 * @param companyID id de la compañia
 * @param branchID id del branch
 * @param pinpadCode código de acceso para la interface de administración en el dispositivo
 * @returns configuración corregida
 */
export function sanitizeVendingConfig(
  loadedConfig: VendingTerminalResponse,
  companyID: number,
  branchID: number,
  pinpadCode: string
): VendingTerminalResponse {
  // Reasignamos campos que antes no existían
  // Asignamos valores a propiedades undefined porque sino Vue no las toma
  const vdata: VendingTerminalResponse = {
    ...loadedConfig,
    companyID,
    branchID,
    pinpadCode
  };
  vdata.topayMessages?.sort((a, b) => a.timestamp - b.timestamp);
  if (!vdata.topay) {
    vdata.topay = false;
    vdata.topayConfig = defaultToPayConfig();
  }
  completeTopayConfig(vdata);
  completePulseConfig(vdata);
  completeMdbConfig(vdata);
  completeVendingProducts(vdata);
  if (!vdata.chargerConfig) {
    vdata.chargerConfig = defaultChargerConfig();
  }
  completeReceiptConfig(vdata);
  if (typeof vdata.appEventsEnabled !== "boolean") {
    vdata.appEventsEnabled = false;
  }
  return vdata;
}

function completeTopayConfig(vdata: VendingConfig) {
  if (!vdata.topayConfig) return;
  if (!vdata.topayConfig.qrCameraType) vdata.topayConfig.qrCameraType = "camera_auto";
  if (typeof vdata.topayConfig.qrFlashOn !== "boolean") vdata.topayConfig.qrFlashOn = false;
  if (!vdata.topayConfig.read) {
    vdata.topayConfig.read = "1";
  }
  if (!vdata.topayConfig.rechargePredefinedAmounts) {
    vdata.topayConfig.rechargePredefinedAmounts = [5, 10, 20, 50];
  }
  vdata.topayConfig.rechargePredefinedAmounts = vdata.topayConfig.rechargePredefinedAmounts.sort((a, b) => a - b);
  if (!vdata.topayConfig.rechargeMinAmount) {
    vdata.topayConfig.rechargeMinAmount = 0;
  }
  if (typeof vdata.topayConfig.rechargeEnableOtherAmount !== "boolean") {
    vdata.topayConfig.rechargeEnableOtherAmount = true;
  }
}

function completePulseConfig(vdata: VendingConfig) {
  if (!vdata.pulseConfig) {
    vdata.pulseConfig = defaultPulseConfig();
  }
  // Fill new properties if missing from old config
  if (isNull(vdata.pulseConfig.topayAutoSale)) {
    vdata.pulseConfig.topayAutoSale = true;
  }
  if (vdata.pulseConfig.enabledByInputSignal == undefined) {
    vdata.pulseConfig.enabledByInputSignal = null;
  }
  if (vdata.pulseConfig.pulseInterval === undefined) {
    vdata.pulseConfig.pulseInterval = 100;
  }
  if (vdata.pulseConfig.productSelectionPolicy === undefined) {
    vdata.pulseConfig.productSelectionPolicy = "firstProduct";
  }
}

function completeReceiptConfig(vdata: VendingConfig) {
  if (!vdata.receiptConfig) {
    vdata.receiptConfig = {
      enabled: !!vdata.unattended,
      enableVirtualReceipt: !!vdata.unattended,
      printWithoutPrompt: false,
      externalPrinter: { enabled: false, paperWidth: null, parameters: null, printerType: null },
      ticketFooter: vdata.unattendedConfig?.ticketFooter ?? "",
      ticketHeader: vdata.unattendedConfig?.ticketHeader ?? "",
      ticketProduct: vdata.unattendedConfig?.ticketProduct ?? ""
    };
    return;
  }
  if (typeof vdata.receiptConfig.printWithoutPrompt !== "boolean") {
    vdata.receiptConfig.printWithoutPrompt = false;
  }
}

function completeVendingProducts(vdata: VendingConfig) {
  for (const p of vdata.products) {
    if (p.pulseCount === undefined) {
      p.pulseCount = 1;
    }
  }
}

function completeMdbConfig(vdata: VendingConfig) {
  if (!vdata.mdbSlaveConfig) {
    vdata.mdbSlaveConfig = defaultMdbSlaveConfig();
    return;
  }
  // Fill new properties if missing from old config
  if (isNull(vdata.mdbSlaveConfig.readerEnabledByDefault)) {
    vdata.mdbSlaveConfig.readerEnabledByDefault = true;
  }
  if (isNull(vdata.mdbSlaveConfig.cardPaymentEnabled)) {
    vdata.mdbSlaveConfig.cardPaymentEnabled = true;
  }
  if (isNull(vdata.mdbSlaveConfig.usesSale)) {
    vdata.mdbSlaveConfig.usesSale = false;
  }
  if (isNull(vdata.mdbSlaveConfig.updateMachinePrices)) {
    vdata.mdbSlaveConfig.updateMachinePrices = false;
  }
  if (isNull(vdata.mdbSlaveConfig.cashSaleIncludesBaseCoin)) {
    vdata.mdbSlaveConfig.cashSaleIncludesBaseCoin = false;
  }
  if ((vdata.mdbSlaveConfig.cashless as string) == "chasless2") {
    // Esto es porque había un typo en configuraciones viejas, entonces hay que corregirlo
    // si no se hace esto no va a seleccionar correctamente el tipo de cashless y puede traer errores
    vdata.mdbSlaveConfig.cashless = "cashless2";
  }
  removeObjectProperty(vdata.mdbSlaveConfig, "deviceLevel");
}

export function newVendingConfig(
  companyID: number,
  branchID: number,
  TCOD: string,
  pinpadCode: string
): VendingTerminalResponse {
  return {
    id: null,
    companyID,
    branchID,
    TCOD,
    pinpadCode,
    baseCoin: 1,
    decimals: 2,
    operationMode: OperationMode.MDBSlave,
    products: [],
    topay: false,
    unattended: false,
    topayConfig: defaultToPayConfig(),
    mdbSlaveConfig: defaultMdbSlaveConfig(),
    chargerConfig: defaultChargerConfig(),
    pulseConfig: defaultPulseConfig(),
    receiptConfig: {
      enabled: false,
      enableVirtualReceipt: false,
      externalPrinter: { enabled: false, paperWidth: null, parameters: null, printerType: null },
      printWithoutPrompt: false,
      ticketFooter: "",
      ticketHeader: "",
      ticketProduct: ""
    },
    topayMessages: null,
    appEventsEnabled: false
  };
}

export function defaultToPayConfig(): ToPayConfig {
  return {
    site: "",
    machine: "",
    signingKey: "",
    showQR: "0",
    read: "1",
    host: "www.vendingart.com",
    visibleCredits: ["0"],
    rechargePredefinedAmounts: [5, 10, 20, 50],
    rechargeMinAmount: 0,
    rechargeEnableOtherAmount: true,
    qrCameraType: "camera_auto",
    qrFlashOn: false,
    ticketQr: null
  };
}

export function defaultPulseConfig(): PulseConfig {
  return {
    ms: 1000,
    sessionTimeout: 60000,
    paymentTimeout: 60000,
    topayAutoSale: true,
    enabledByInputSignal: null,
    pulseInterval: 100,
    productSelectionPolicy: "firstProduct"
  };
}

export function defaultMdbSlaveConfig(): MDBSlaveConfig {
  return {
    cashless: "cashless1",
    remoteVend: false,
    sessionTimeout: 60000,
    alwaysCredit: false,
    lineGrouping: false,
    alwaysIdle: false,
    creditCharge: false,
    pricesInMachine: true,
    lineOffset: 0,
    vendTimeout: 60000,
    paymentTimeout: 60000,
    defaultCredit: 0,
    validationDelay: false,
    initialPreauth: false,
    cashSaleIncludesBaseCoin: false,
    readerEnabledByDefault: true,
    usesSale: false,
    cardPaymentEnabled: true,
    updateMachinePrices: false
  };
}

export function defaultChargerConfig(): ChargerConfig {
  return {
    sessionTimeout: 60000,
    creditCharge: false,
    paymentTimeout: 50000
  };
}

export function prepareVendingConfigForSave(configToSave: VendingConfig): VendingConfig {
  const config = {
    ...configToSave
  };
  if (
    config.operationMode == OperationMode.MDBSlave &&
    config.mdbSlaveConfig?.pricesInMachine &&
    config.mdbSlaveConfig.lineGrouping
  ) {
    config.products.forEach((p) => (p.cardPrice = p.cashPrice));
  }
  config.decimals = 2;
  config.topay = !!config.topay;
  if (isNull(config.appEventsEnabled)) {
    config.appEventsEnabled = false;
  }
  if (!config.topay) {
    config.topayConfig = null;
  }
  switch (config.operationMode) {
    case OperationMode.MDBSlave:
      config.pulseConfig = null;
      config.chargerConfig = null;
      break;
    case OperationMode.Pulse:
      config.mdbSlaveConfig = null;
      config.chargerConfig = null;
      break;
    case OperationMode.Recharger:
      config.mdbSlaveConfig = null;
      config.pulseConfig = null;
      break;
  }
  removeObjectProperty(config, "topayMessages");
  removeObjectProperty(config, "reset");
  return config;
}
